import { Box, Button, Divider, Fab, Tooltip, styled, IconButton } from "@mui/material";
import { useRef } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

export const RightSideInnerContent = ({
    stepContentState, mediaInputRef, handleMediaUpload, removeMedia, lngsltd, handleVideoUpload
}) => {
    switch (stepContentState.activeContentType) {
        case 'Image':
            return (
                <ImagesRendrer
                    mediaInputRef={mediaInputRef}
                    handleMediaUpload={handleMediaUpload}
                    imageUrls={stepContentState.imageUrls}
                    removeMedia={removeMedia}
                    lngsltd={lngsltd}
                />
            );
        case 'Video':
            return (
                <VideoRendrer
                    handleVideoUpload={handleVideoUpload}
                    videoUrls={stepContentState.videoUrls}
                    lngsltd={lngsltd}
                    removeMedia={removeMedia}
                />
            );

        default:
            return null;
    }

}

function ImagesRendrer({ mediaInputRef, handleMediaUpload, imageUrls, removeMedia, lngsltd }) {
    return (
        <Box width="100%" display="flex" flexDirection="column" gap={2}>
            <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-start' }} alignItems="center" width="100%">
                <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    multiple
                    ref={mediaInputRef}
                    onChange={handleMediaUpload} />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ImageIcon sx={{ fontSize: '1rem' }} />}
                    onClick={() => mediaInputRef.current.click()}
                    sx={{ 
                        minWidth: 'auto',
                        px: 2,
                        py: 0.75,
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#dc3545',
                            color: '#dc3545',
                            backgroundColor: 'rgba(220, 53, 69, 0.05)',
                            borderWidth: '1.5px'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Choose Image
                </Button>
            </Box>
            {imageUrls?.length > 0 && (
                <Box 
                    display="flex" 
                    flexWrap="wrap" 
                    gap={1.5}
                    sx={{
                        justifyContent: { xs: 'center', sm: 'flex-start' },
                        mt: 1.5
                    }}
                >
                    {imageUrls.map((imageUrl, i) => (
                        <Box 
                            key={i} 
                            position="relative"
                            sx={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    '& .delete-fab': {
                                        opacity: 1
                                    }
                                }
                            }}
                        >
                            <img 
                                alt="media" 
                                src={imageUrl} 
                                style={{ 
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'cover',
                                    display: 'block'
                                }} 
                            />
                            <Tooltip title={lngsltd["Delete"]} arrow>
                                <Fab
                                    className="delete-fab"
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={() => removeMedia(i)}
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 6, 
                                        right: 6,
                                        width: '28px',
                                        height: '28px',
                                        minHeight: '28px',
                                        opacity: 0.9,
                                        boxShadow: '0 2px 6px rgba(220, 53, 69, 0.3)',
                                        '&:hover': {
                                            backgroundColor: '#b02a37',
                                            boxShadow: '0 3px 8px rgba(220, 53, 69, 0.4)',
                                            opacity: 1
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                                </Fab>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

const VideoPreviewBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: { xs: '180px', sm: '220px', md: '260px' },
    height: { xs: '120px', sm: '150px', md: '180px' },
    minWidth: '180px',
    minHeight: '120px',
    backgroundColor: theme.palette.grey[200],
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.grey[300],
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
}));

function VideoRendrer({ handleVideoUpload, videoUrls, lngsltd, removeMedia }) {
    const fileInputRef = useRef(null);

    return (
        <Box width="100%" display="flex" flexDirection="column" gap={2}>
            <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-start' }} alignItems="center" width="100%">
                <input
                    type="file"
                    accept='video/*'
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleVideoUpload} />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VideoLibraryIcon sx={{ fontSize: '1rem' }} />}
                    onClick={() => fileInputRef.current.click()}
                    sx={{ 
                        minWidth: 'auto',
                        px: 2,
                        py: 0.75,
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        borderRadius: '6px',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: '#dc3545',
                            color: '#dc3545',
                            backgroundColor: 'rgba(220, 53, 69, 0.05)',
                            borderWidth: '1.5px'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Choose Video
                </Button>
            </Box>
            {videoUrls?.length > 0 && (
                <Box 
                    display="flex" 
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                    width="100%"
                    sx={{ mt: 1.5 }}
                >
                    <Box position="relative">
                        <VideoPreviewBox>
                            <IconButton
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    width: { xs: '48px', sm: '56px' },
                                    height: { xs: '48px', sm: '56px' },
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <PlayArrowIcon sx={{ fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                            </IconButton>
                        </VideoPreviewBox>

                        <Tooltip title={lngsltd["Remove Video"] || "Remove Video"} arrow>
                            <Fab
                                className="delete-fab"
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => removeMedia(0)}
                                sx={{ 
                                    position: 'absolute', 
                                    top: 6, 
                                    right: 6,
                                    width: '28px',
                                    height: '28px',
                                    minHeight: '28px',
                                    opacity: 0.9,
                                    boxShadow: '0 2px 6px rgba(220, 53, 69, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#b02a37',
                                        boxShadow: '0 3px 8px rgba(220, 53, 69, 0.4)',
                                        opacity: 1
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                            </Fab>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Box>
    );
}